import React, { useMemo, useState } from "react";
import {
  GitBranch, BookOpen, ShieldCheck, Lock, Table2, Network, Grid3x3, AlertTriangle, Sparkles, ServerCog,
} from "lucide-react";
import { atomsForCell } from "./api";
import {
  Panel, SectionHead, StatusBadge, KeyBadge, Row, cellVal, Drawer, DataTable, StatusLegend, AwaitingSource,
  useModel, useAri, useTaxonomy, humanize,
} from "./shared";
import { ARI_STATUS, ARI_DOMAIN_TABLES } from "./ari";
import { AVAILABILITY } from "./menuTaxonomy";

/* ---------------- Source Evidence & Lineage ---------------- */
export function SourceEvidencePage() {
  const model = useModel();
  const chain = [["report_instances", "Reports"], ["statement_instances", "Statements"], ["source_rows", "Rows"], ["source_cells", "Cells"], ["source_atoms", "Atoms"]];
  return (
    <div className="space-y-6">
      <SectionHead eyebrow="Evidence" title="Source Evidence & Lineage"
        caption="The governed provenance chain from report to atom. Every business fact retains its company / report / page / block evidence." />
      <StatusLegend statuses={["EXTRACTED_RAW", "PAGE_PROVEN_ATOM_PENDING", "CERTIFIED"]} />
      <Panel>
        <div className="flex flex-wrap items-center gap-2" data-testid="lineage-flow">
          {chain.map(([tbl, label], i) => (
            <React.Fragment key={tbl}>
              <div className="flex-1 rounded-lg border border-slate-200 bg-slate-50 p-3 text-center">
                <div className="font-extrabold text-slate-800">{label}</div><div className="font-mono text-[10px] text-indigo-600">{tbl}</div>
                <div className="text-2xl font-extrabold text-slate-900">{(model.records[tbl] || []).length}</div>
              </div>
              {i < chain.length - 1 && <span className="text-xl font-extrabold text-indigo-500">→</span>}
            </React.Fragment>
          ))}
        </div>
        <div className="mt-2 text-xs text-slate-500">Lineage edges: <b>{model.records.lineage_edges.length}</b> cell→atom links · header-period evidence <b>{model.records.header_period_evidence.length}</b> · structural eligibility <b>{model.records.structural_eligibility.length}</b></div>
      </Panel>
      <div className="grid gap-6 lg:grid-cols-2">
        <Panel><div className="mb-2 font-bold text-slate-800">Lineage edges (cell → atom)</div><DataTable testid="data-lineage-table" records={model.records.lineage_edges} pageSize={8} /></Panel>
        <Panel><div className="mb-2 font-bold text-slate-800">Evidence artifacts</div><DataTable testid="evidence-artifacts-table" records={model.records.evidence_artifacts} pageSize={8} /></Panel>
        <Panel><div className="mb-2 font-bold text-slate-800">Header period evidence</div><DataTable testid="header-evidence-table" records={model.records.header_period_evidence} pageSize={8} /></Panel>
        <Panel><div className="mb-2 font-bold text-slate-800">Source atoms</div><DataTable testid="source-atoms-table" records={model.records.source_atoms} pageSize={8} /></Panel>
      </div>
    </div>
  );
}

/* ---------------- Data Dictionary & New Findings ---------------- */
function FieldDictionary({ model, dictTable, setDictTable }) {
  const t = model.tables.find((x) => x.table === dictTable) || model.tables[0];
  const sample = (model.records[t.table] || [])[0] || {};
  return (
    <div>
      <div className="mb-3 flex items-center gap-3">
        <select data-testid="dictionary-select" value={t.table} onChange={(e) => setDictTable(e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm">
          {model.tables.map((x) => <option key={x.table} value={x.table}>{x.displayName} ({x.table})</option>)}
        </select>
        <span className="text-sm text-slate-500">{t.fields.length} fields · {t.rowCount} rows</span>
      </div>
      <Panel className="mb-3"><div className="font-extrabold text-slate-900">{t.displayName} <span className="font-mono text-xs text-indigo-600">{t.table}</span></div><p className="mt-1 text-sm text-slate-500">{t.description}</p></Panel>
      <div className="overflow-auto rounded-lg border border-slate-200" style={{ maxHeight: 620 }}>
        <table className="w-full text-sm" data-testid="field-dictionary-table">
          <thead className="sticky top-0 bg-slate-50"><tr>
            {["Field / Label", "Type", "Nullable", "Key", "References", "Description", "Example", "Provenance"].map((h) => <th key={h} className="px-3 py-2 text-left font-bold text-slate-700">{h}</th>)}
          </tr></thead>
          <tbody>
            {t.fields.map((f) => (
              <tr key={f.name} data-testid={`field-${f.name}`} className="border-b border-slate-100">
                <td className="px-3 py-2"><div className="font-semibold text-slate-800">{humanize(f.name)}</div><div className="font-mono text-[11px] text-indigo-600">{f.name}</div></td>
                <td className="px-3 py-2"><code className="text-xs">{f.type}</code></td>
                <td className="px-3 py-2">{f.required ? <span className="rounded border border-amber-300 px-1.5 text-xs text-amber-700">required</span> : <span className="rounded border border-slate-200 px-1.5 text-xs text-slate-500">nullable</span>}</td>
                <td className="px-3 py-2">{f.isPrimaryKey && <KeyBadge kind="PK" />}{f.isForeignKey && <KeyBadge kind="FK" />}{!f.isPrimaryKey && !f.isForeignKey && "—"}</td>
                <td className="px-3 py-2 font-mono text-xs">{f.isForeignKey ? `${f.referencedTable}.${f.referencedField}` : "—"}</td>
                <td className="max-w-xs px-3 py-2 text-xs text-slate-500">{f.description}</td>
                <td className="max-w-[180px] truncate px-3 py-2 text-xs">{cellVal(sample[f.name])}</td>
                <td className="px-3 py-2 text-xs text-slate-500">{f.provenance}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function DataDictionaryPage() {
  const model = useModel();
  const [tab, setTab] = useState("catalogue");
  const [dictTable, setDictTable] = useState(model.tables[0]?.table);
  const tabs = [
    { id: "catalogue", label: "Table Catalogue", icon: Table2 },
    { id: "dictionary", label: "Field Dictionary", icon: BookOpen },
    { id: "findings", label: "New Findings", icon: Sparkles },
  ];
  return (
    <div>
      <SectionHead eyebrow="Metadata" title="Data Dictionary & New Findings"
        caption="Contract-driven catalogue generated from governed schemas and the data dictionary. Genuinely new fields surface as Schema Evolution Candidates." />
      <div className="mb-4 flex flex-wrap gap-1 rounded-xl border border-slate-200 bg-white p-1">
        {tabs.map((t) => (
          <button key={t.id} data-testid={`dict-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold ${tab === t.id ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
            <t.icon className="h-4 w-4" />{t.label}
          </button>
        ))}
      </div>
      {tab === "catalogue" && (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="table-catalogue">
          {model.tables.map((t) => (
            <Panel key={t.table}>
              <div className="flex items-start justify-between">
                <div><div className="font-extrabold text-slate-900">{t.displayName}</div><div className="font-mono text-xs text-indigo-600">{t.table}</div></div>
                <span className="rounded bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-600">{t.canonical === false ? "DERIVED" : "CANONICAL"}</span>
              </div>
              <p className="my-2 min-h-[48px] text-sm text-slate-500">{t.description}</p>
              <dl className="space-y-1 text-xs">
                <Row k="Grain" v={t.grain} /><Row k="Rows" v={String(t.rowCount)} /><Row k="Fields" v={String(t.fields.length)} />
                <Row k="Foreign keys" v={t.foreignKeys.length ? t.foreignKeys.map((f) => f.field).join(", ") : "—"} />
              </dl>
              <button data-testid={`catalogue-open-${t.table}`} onClick={() => { setDictTable(t.table); setTab("dictionary"); }} className="mt-3 w-full rounded-lg border border-slate-200 py-1.5 text-sm font-semibold text-indigo-600 hover:bg-indigo-50">Open Field Dictionary →</button>
            </Panel>
          ))}
        </div>
      )}
      {tab === "dictionary" && <FieldDictionary model={model} dictTable={dictTable} setDictTable={setDictTable} />}
      {tab === "findings" && (
        <div className="space-y-4">
          <Panel className="flex items-start gap-3">
            <div className="rounded-lg bg-indigo-50 p-2 text-indigo-700"><Sparkles className="h-5 w-5" /></div>
            <div className="text-sm text-slate-600"><b>Schema Evolution Candidates</b> highlight genuinely new fields discovered during extraction that are not yet part of the certified contract. They are sourced from the ari_source <span className="font-mono">field_dictionary</span>, <span className="font-mono">field_observations</span> and <span className="font-mono">schema_evolution_log</span> tables.</div>
          </Panel>
          <AwaitingSource tables={ARI_DOMAIN_TABLES.dictionary} columns={["Field", "Observed in", "Occurrences", "Proposed type", "Status"]} testid="findings-awaiting" />
        </div>
      )}
    </div>
  );
}

/* ---------------- Review & Data Quality (admin) ---------------- */
function RelationshipMatrix({ model, onRel }) {
  const [q, setQ] = useState("");
  const rels = model.relationships.filter((r) => `${r.parentTable} ${r.childTable} ${r.cardinality} ${r.description}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div>
      <div className="mb-2"><input data-testid="matrix-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search relationships…" className="w-72 rounded-lg border border-slate-200 px-3 py-1.5 text-sm" /></div>
      <div className="overflow-auto rounded-lg border border-slate-200">
        <table className="w-full text-sm" data-testid="relationship-matrix-table">
          <thead className="bg-slate-50"><tr>{["Parent", "Parent field", "Child", "Child field", "Cardinality", "Matched", "Orphans", "Integrity"].map((h) => <th key={h} className="px-3 py-2 text-left font-bold text-slate-700">{h}</th>)}</tr></thead>
          <tbody>
            {rels.map((r) => (
              <tr key={`${r.parentTable}.${r.parentField}-${r.childTable}.${r.childField}`} data-testid="matrix-row" onClick={() => onRel(r)} className="cursor-pointer border-b border-slate-100 hover:bg-indigo-50/40">
                <td className="px-3 py-2 font-mono text-xs">{r.parentTable}</td><td className="px-3 py-2 font-mono text-xs">{r.parentField}</td>
                <td className="px-3 py-2 font-mono text-xs">{r.childTable}</td><td className="px-3 py-2 font-mono text-xs">{r.childField}</td>
                <td className="px-3 py-2"><span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-bold">{r.cardinality}</span></td>
                <td className="px-3 py-2">{r.matchedCount}</td>
                <td className={`px-3 py-2 font-bold ${r.orphanCount ? "text-rose-600" : "text-emerald-600"}`}>{r.orphanCount}</td>
                <td className="px-3 py-2"><StatusBadge value={r.integrity} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function ReviewQualityPage() {
  const model = useModel();
  const [tab, setTab] = useState("certification");
  const [reviews, setReviews] = useState({});
  const [relDialog, setRelDialog] = useState(null);
  const run = model.records.certification_runs[0];
  const gate = model.records.certification_gate_summary[0];
  const e = model.evidence;
  const exRows = model.records.source_rows.filter(
    (r) => (model.records.structural_eligibility.find((x) => x.source_row_id === r.id)?.classification !== "FINANCIAL_ELIGIBLE")
  );
  const setRev = (id, s) => setReviews((p) => ({ ...p, [id]: s }));
  const tabs = [
    { id: "certification", label: "Certification", icon: ShieldCheck },
    { id: "exceptions", label: "Exceptions & Review", icon: AlertTriangle },
    { id: "relationships", label: "Relationship Matrix", icon: Grid3x3 },
    { id: "heldout", label: "Heldout Firewall", icon: Lock },
  ];
  const Metric = ({ label, value }) => (<Panel className="flex-1"><div className="text-[11px] font-bold uppercase text-slate-400">{label}</div><div className="mt-1 text-2xl font-extrabold text-slate-900">{value}</div></Panel>);
  return (
    <div>
      <SectionHead eyebrow="Administrator" title="Review & Data Quality"
        caption="Technical certification and data-quality controls. Statuses are shown exactly as produced by the pipeline — FAIL and NOT_RUN are preserved (fail-closed)." />
      <div className="mb-4 flex flex-wrap gap-1 rounded-xl border border-slate-200 bg-white p-1">
        {tabs.map((t) => (
          <button key={t.id} data-testid={`rq-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold ${tab === t.id ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
            <t.icon className="h-4 w-4" />{t.label}
          </button>
        ))}
      </div>

      {tab === "certification" && (
        <div className="space-y-6">
          <div className="flex flex-wrap gap-3">
            <Metric label="Run status" value={run ? <StatusBadge value={run.run_status || run.status} /> : "—"} />
            <Metric label="Gate summary" value={gate ? <StatusBadge value={gate.status} /> : "—"} />
            <Metric label="Referential integrity" value={<StatusBadge value={e.referentialIntegrity} />} />
            <Metric label="Orphans" value={e.orphanRecordCount} />
          </div>
          {gate && (
            <Panel>
              <div className="mb-2 font-bold text-slate-800">Certification gates</div>
              <div className="grid gap-2 md:grid-cols-2">
                {Object.entries(gate).filter(([k]) => !["id", "status", "reason"].includes(k)).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between rounded border border-slate-100 px-3 py-1.5 text-sm"><span className="text-slate-600">{humanize(k)}</span><StatusBadge value={v} /></div>
                ))}
              </div>
              {gate.reason && <div className="mt-2 text-xs text-slate-500">Reason: {gate.reason}</div>}
            </Panel>
          )}
          <div className="grid gap-6 lg:grid-cols-2">
            <Panel><div className="mb-2 font-bold text-slate-800">Pipeline stages</div><DataTable testid="stages-table" records={model.records.pipeline_stage_runs} pageSize={8} /></Panel>
            <Panel><div className="mb-2 font-bold text-slate-800">Semantic & Determinism</div><DataTable testid="semantic-table" records={[...model.records.semantic_comparison_results, ...model.records.determinism_artifacts]} pageSize={8} /></Panel>
            <Panel><div className="mb-2 font-bold text-slate-800">Tests & Builds</div><DataTable testid="tests-table" records={[...model.records.test_execution_results, ...model.records.build_execution_results]} pageSize={8} /></Panel>
            <Panel><div className="mb-2 font-bold text-slate-800">Evidence artifacts</div><DataTable testid="evidence-table" records={model.records.evidence_artifacts} pageSize={8} /></Panel>
          </div>
        </div>
      )}

      {tab === "exceptions" && (
        <Panel>
          <div className="mb-2 text-sm text-slate-500">{exRows.length} rows flagged for review · decisions are session-local and never alter governed values</div>
          <div className="overflow-auto rounded-lg border border-slate-200" style={{ maxHeight: 560 }}>
            <table className="w-full text-sm" data-testid="exceptions-table">
              <thead className="sticky top-0 bg-slate-50"><tr>
                <th className="px-3 py-2 text-left font-bold text-slate-700">Line item</th><th className="px-3 py-2 text-left font-bold text-slate-700">Basis</th>
                <th className="px-3 py-2 text-left font-bold text-slate-700">Classification</th><th className="px-3 py-2 text-center font-bold text-slate-700">Page</th>
                <th className="px-3 py-2 text-left font-bold text-slate-700">Review</th>
              </tr></thead>
              <tbody>
                {exRows.map((r) => {
                  const cls = model.records.structural_eligibility.find((x) => x.source_row_id === r.id)?.classification || "UNCLASSIFIED";
                  return (
                    <tr key={r.id} data-testid="exception-row" className="border-b border-slate-100">
                      <td className="px-3 py-2 font-medium text-slate-800">{r.raw_text}</td>
                      <td className="px-3 py-2">{cellVal(r.basis)}</td>
                      <td className="px-3 py-2"><StatusBadge value={cls} /></td>
                      <td className="px-3 py-2 text-center text-slate-500">{cellVal(r.page)}</td>
                      <td className="px-3 py-2"><div className="flex gap-1">
                        {["ACCEPT", "REJECT", "NEEDS_INFO"].map((a) => (
                          <button key={a} data-testid={`review-${a}-${r.id}`} onClick={() => setRev(r.id, a)}
                            className={`rounded px-2 py-1 text-xs font-semibold ${reviews[r.id] === a ? "bg-indigo-600 text-white" : "border border-slate-200 text-slate-600"}`}>{a.replace("_", " ")}</button>
                        ))}
                      </div></td>
                    </tr>
                  );
                })}
                {exRows.length === 0 && <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-400">No exceptions — all rows are financially eligible.</td></tr>}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      {tab === "relationships" && <Panel><RelationshipMatrix model={model} onRel={setRelDialog} /></Panel>}

      {tab === "heldout" && (
        <Panel>
          <div className="mb-2 flex items-center gap-2 font-bold text-slate-800"><Lock className="h-4 w-4 text-amber-600" />Heldout firewall</div>
          <div className="mb-2 rounded bg-amber-50 px-3 py-2 text-sm text-amber-800">ICICI and SBI corpora remain sealed. This module only audits access.</div>
          <DataTable testid="heldout-table" records={model.records.heldout_access_audit} pageSize={8} />
        </Panel>
      )}

      <Drawer open={Boolean(relDialog)} onClose={() => setRelDialog(null)} title="Relationship definition">
        {relDialog && (
          <div className="space-y-2 text-sm" data-testid="relationship-dialog">
            {[["Parent", `${relDialog.parentTable}.${relDialog.parentField}`], ["Child", `${relDialog.childTable}.${relDialog.childField}`],
              ["Cardinality", relDialog.cardinality], ["Mandatory", relDialog.mandatory], ["Description", relDialog.description],
              ["Matched", relDialog.matchedCount], ["Orphans", relDialog.orphanCount]].map(([k, v]) => (
              <div key={k} className="flex justify-between gap-3"><span className="text-slate-500">{k}</span><span className="text-right font-medium text-slate-800">{String(v)}</span></div>
            ))}
            <div className="flex justify-between"><span className="text-slate-500">Referential integrity</span><StatusBadge value={relDialog.integrity} /></div>
          </div>
        )}
      </Drawer>
    </div>
  );
}

/* ---------------- Data Access & Preflight (admin) ---------------- */
export function DataAccessPage() {
  const ari = useAri();
  const tax = useTaxonomy();
  const pre = ari?.preflight;
  const groups = ari?.sourceTables?.groups || {};
  const ready = pre?.DATA_ACCESS_READY || "PARTIAL";
  const st = tax?.status;
  return (
    <div className="space-y-6">
      <SectionHead eyebrow="Administrator" title="Data Access & Preflight"
        caption="Governed handoff integrity and schema readiness. The product is API-first with a governed static fallback; missing dependencies are named, never masked." />
      <div className="flex flex-wrap gap-3">
        <Panel className="flex-1" data-testid="da-active-source"><div className="text-[11px] font-bold uppercase text-slate-400">Active source</div><div className={`mt-1 text-xl font-extrabold ${ari?.activeSource === "GOVERNED_COMMON_API" ? "text-emerald-600" : "text-amber-600"}`}>{ari?.activeSource}</div></Panel>
        <Panel className="flex-1"><div className="text-[11px] font-bold uppercase text-slate-400">Data access ready</div><div className={`mt-1 text-xl font-extrabold ${ready === "YES" ? "text-emerald-600" : "text-amber-600"}`}>{ready}</div></Panel>
        <Panel className="flex-1"><div className="text-[11px] font-bold uppercase text-slate-400">ari schema</div><div className="mt-1 text-xl font-extrabold text-slate-900">18 tables</div></Panel>
        <Panel className="flex-1"><div className="text-[11px] font-bold uppercase text-slate-400">ari_source schema</div><div className="mt-1 text-xl font-extrabold text-slate-900">37 tables</div></Panel>
      </div>

      {st && (
        <Panel data-testid="preflight-module-status">
          <div className="mb-3 flex items-center justify-between">
            <div className="font-bold text-slate-800">Module coverage (canonical taxonomy)</div>
            <div className="text-xs text-slate-500">{st.total} modules · {st.applicable} applicable · {st.pct}% covered</div>
          </div>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
            {["AVAILABLE", "PARTIAL", "AWAITING_GOVERNED_SOURCE", "NOT_APPLICABLE"].map((s) => (
              <div key={s} className="rounded-lg border border-slate-200 p-2">
                <div data-testid={`canonical-count-${s}`} className="text-xl font-extrabold text-slate-900">{st.byState[s]}</div>
                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ring-1 ring-inset ${AVAILABILITY[s].chip}`}><span className={`h-1.5 w-1.5 rounded-full ${AVAILABILITY[s].dot}`} />{AVAILABILITY[s].label}</span>
              </div>
            ))}
          </div>
        </Panel>
      )}

      {pre && (
        <Panel data-testid="preflight-report">
          <div className="mb-2 flex items-center gap-2 font-bold text-slate-800"><ServerCog className="h-4 w-4 text-indigo-600" />Preflight report</div>
          <div className="grid gap-2 md:grid-cols-2">
            {Object.entries(pre).map(([k, v]) => (
              <div key={k} className="rounded border border-slate-100 px-3 py-1.5 text-sm">
                <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{k}</div>
                <div className="text-slate-700">{typeof v === "object" ? <span className="font-mono text-xs">{JSON.stringify(v)}</span> : String(v)}</div>
              </div>
            ))}
          </div>
        </Panel>
      )}

      <StatusLegend statuses={ARI_STATUS} />

      <Panel data-testid="source-tables-grid">
        <div className="mb-3 font-bold text-slate-800">ari_source table availability</div>
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {Object.entries(groups).map(([group, list]) => (
            <div key={group} className="rounded-lg border border-slate-200 p-3">
              <div className="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-400">{humanize(group)}</div>
              <div className="space-y-1">
                {(list || []).map((t) => (
                  <div key={t.table} className="flex items-center justify-between text-xs">
                    <span className="font-mono text-slate-600">{t.table}</span>
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold ${t.available ? "bg-emerald-50 text-emerald-700" : "bg-stone-100 text-stone-500"}`}>{t.available ? `${t.row_count} rows` : "awaiting"}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}
